import React from "react";
import { useTranslation } from "react-i18next";
import { useMachine } from "@xstate/react";
import truncate from "lodash/truncate";
import { LoadingProgressBar, PageLayout } from "@components";
import { useMandatorLinks } from "@hooks";
import { SomethingWrong } from "./steps/SomethingWrong";
import { SearchSymbolStep } from "./steps/SearchSymbolStep";
import { PriceAndDateStep } from "./steps/PriceAndDateStep";
import { BookingValues } from "./stateMachine/types/bookingValues";
import { SummaryStep } from "./steps/SummaryStep";
import { BookingTypeStep } from "./steps/BookingTypeStep";
import { PersistedStep } from "./steps/PersistedStep";
import { useStateHelpers } from "./hooks/useStateHelpers";
import { addTransactionWizardSM } from "./stateMachine/addTransactionWizardSM";
import { SplitPreviewStep } from "./steps/SplitPreviewStep";
import {
  BookingTypeWithSplit,
  SplitValues,
} from "./stateMachine/types/splitValues";
import { RatioAndDateStep } from "./steps/RatioAndDateStep";
import type { Symbol } from "@bn/api/apiv1";

export interface Props {
  investmentId: number;
}

export const AddTransactionWizard = ({ investmentId }: Props) => {
  const [currentState, sendEvent] = useMachine(addTransactionWizardSM);
  const { isLoadingState } = useStateHelpers(currentState);

  const backOneStep = () => sendEvent({ type: "BACK" });

  const handleSelectBookingType = (bookingType: BookingTypeWithSplit) => {
    if (bookingType === "split") {
      sendEvent({ type: "SPLIT_SELECTED" });
      return;
    }

    ["carve_out", "switch"].includes(bookingType)
      ? sendEvent({
          type: "CAPITAL_MEASURE_SELECTED",
          bookingType: bookingType,
        })
      : sendEvent({
          type: "BOOKING_TYPE_SELECTED",
          bookingType: bookingType,
        });
  };

  const { t } = useTranslation();
  const { getInstrumentDetailUrl } = useMandatorLinks();

  const investmentValues = currentState.context.investmentValues;
  const instrumentDetailUrl = getInstrumentDetailUrl?.({
    type: investmentValues?.type,
    isin: investmentValues?.isin,
    quoteProvider: investmentValues?.quoteProvider,
  });

  return (
    <PageLayout
      breadcrumbs={[
        { text: t("breadcrumbs.home"), to: "/" },
        { to: "/investments", text: t("breadcrumbs.investments") },
        ...(investmentValues?.name
          ? [
              {
                href: instrumentDetailUrl ?? undefined,
                text: truncate(investmentValues.name),
              },
            ]
          : []),
        { text: t("transactionWizard.add.title") },
      ]}
    >
      {isLoadingState() && <LoadingProgressBar />}

      {currentState.matches("idle") &&
        sendEvent({ type: "INIT_FROM_INVESTMENT", id: investmentId }) && (
          <LoadingProgressBar />
        )}

      {currentState.matches("selectBookingType") && (
        <BookingTypeStep
          defaultValue={currentState.context.bookingValues.type}
          investmentType={
            currentState.context.investmentValues.type || "11_stock"
          }
          handleNext={handleSelectBookingType}
          isNewInvestment={false}
        />
      )}

      {currentState.matches("searchSymbol") && (
        <SearchSymbolStep
          handleNext={(symbol: Symbol | SymbolWithQuote) => {
            sendEvent({ type: "INIT_FROM_SYMBOL", symbol: symbol });
          }}
          handlePrevious={backOneStep}
        />
      )}

      {currentState.matches("priceAndDate") && (
        <PriceAndDateStep
          handleNext={(bookingValues: BookingValues, currency?: string) =>
            sendEvent({
              type: "VALIDATE_BOOKING",
              bookingValues,
              currency,
            })
          }
          handlePrevious={backOneStep}
          bookingValues={currentState.context.bookingValues}
          investmentValues={currentState.context.investmentValues}
          validationError={currentState.context.validationError}
        />
      )}

      {currentState.matches("split.ratioAndDate") && (
        <RatioAndDateStep
          splitValues={currentState.context.splitValues || {}}
          investmentName={currentState.context.investmentValues.name || ""}
          handleNext={(splitValues: SplitValues) =>
            sendEvent({
              type: "PREVIEW",
              splitValues,
            })
          }
          handlePrevious={backOneStep}
          validationError={currentState.context.validationError}
        />
      )}

      {currentState.matches("summary") && (
        <SummaryStep
          handleNext={() =>
            sendEvent({
              type: "PERSIST",
            })
          }
          handlePrevious={backOneStep}
          bookingValues={currentState.context.bookingValues}
          investmentType={
            currentState.context.investmentValues.type || "11_stock"
          }
          investmentCurrency={
            currentState.context.investmentValues.investmentCurrency || "EUR"
          }
        />
      )}

      {currentState.matches("split.preview") && (
        <SplitPreviewStep
          handleNext={() =>
            sendEvent({
              type: "PERSIST",
            })
          }
          handlePrevious={backOneStep}
          splitValues={currentState.context.splitValues || {}}
        />
      )}

      {currentState.matches("persisted") && (
        <PersistedStep
          redirectUrl="/investments"
          investmentName={
            currentState.context.sourceInvestmentValues?.name ||
            currentState.context.investmentValues.name ||
            ""
          }
        />
      )}

      {currentState.matches("somethingWrong") && (
        <SomethingWrong message={currentState.context.error?.message} />
      )}
    </PageLayout>
  );
};
